import React, { useEffect, useMemo, useState } from "react";
import { Search, MapPin, Plane, TrainFront, AlertCircle } from "lucide-react";
import Modal from "@/components/Modal";
import { api } from "@/lib/api";
import { searchPlaces } from "@/lib/places";

const MCO_PLACE = {
  label: "Orlando International Airport (MCO)",
  address: "1 Jeff Fuqua Blvd, Orlando, FL 32827",
  city: "Orlando", state: "FL", lat: 28.4312, lng: -81.3081, place_id: "mco", airport: true,
};

const BRIGHTLINE_PLACE = {
  label: "Brightline Orlando Station",
  address: "10705 Jeff Fuqua Blvd, Orlando, FL 32827 (MCO Terminal C)",
  city: "Orlando", state: "FL", lat: 28.4145, lng: -81.3082, place_id: "brightline", station: true,
};

const HUBS = [MCO_PLACE, BRIGHTLINE_PLACE];

function nearHub(p) {
  if (!p || p.lat == null) return false;
  const R = 3958.8, toR = Math.PI / 180;
  return HUBS.some((h) => {
    const dLat = (p.lat - h.lat) * toR, dLng = (p.lng - h.lng) * toR;
    const hav = Math.sin(dLat / 2) ** 2 + Math.cos(p.lat * toR) * Math.cos(h.lat * toR) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.asin(Math.sqrt(hav)) < 2;
  });
}

export default function PlacePickerModal({ open, onClose, onSelect, title, onlyHub, blockHub }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) { setQ(""); setResults([]); setError(null); }
  }, [open]);

  useEffect(() => {
    if (!open || onlyHub) return;
    let active = true;
    setLoading(true);
    setError(null);
    // Debounce so we don't hit the places API on every keystroke.
    const t = setTimeout(async () => {
      try {
        // Search from the browser (Mapbox Search Box) so it uses the same token
        // as the map and passes URL restrictions; fall back to the backend.
        let places;
        try {
          places = await searchPlaces(q);
        } catch {
          const r = await api(`/places?q=${encodeURIComponent(q)}`);
          places = r.places || [];
          if (r.error) throw new Error(r.error);
        }
        if (!active) return;
        setResults(places);
        setError(null);
      } catch {
        if (active) { setResults([]); setError("Search is temporarily unavailable. Please try again."); }
      } finally {
        if (active) setLoading(false);
      }
    }, 320);
    return () => { active = false; clearTimeout(t); };
  }, [q, open, onlyHub]);

  const list = useMemo(() => {
    if (onlyHub) return HUBS;
    return blockHub ? results.filter((p) => !nearHub(p)) : results;
  }, [onlyHub, blockHub, results]);

  const choose = (p) => {
    onSelect({
      lat: p.lat, lng: p.lng, label: p.label, airport: !!p.airport, station: !!p.station,
      place_id: p.place_id, address: p.address, city: p.city, state: p.state,
    });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={title || "Choose a place"} size="md">
      <div className="mb-4 flex items-center gap-2 rounded-xl border border-border bg-secondary px-3">
        <Search className="h-5 w-5 text-muted-foreground" />
        <input
          data-testid="place-search-input"
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          disabled={onlyHub}
          placeholder={onlyHub ? "Choose the airport or train station" : "Search a hotel, address, or place…"}
          className="h-14 w-full bg-transparent text-base text-foreground outline-none placeholder:text-muted-foreground disabled:opacity-70"
        />
      </div>

      {onlyHub && (
        <p className="mb-3 text-xs text-muted-foreground">Every trip must start or end at Orlando International Airport (MCO) or Brightline Orlando Station.</p>
      )}
      {!onlyHub && blockHub && (
        <p className="mb-3 text-xs text-muted-foreground" data-testid="block-hub-note">The other end is already the airport / station — choose a different place.</p>
      )}

      <div className="max-h-[42vh] space-y-1 overflow-y-auto">
        {loading && list.length === 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">Searching…</p>
        )}
        {!loading && error && (
          <div className="flex items-center gap-2 rounded-xl bg-destructive/10 px-3 py-2.5 text-sm font-medium text-destructive" data-testid="place-search-error">
            <AlertCircle className="h-4 w-4 shrink-0" /> {error}
          </div>
        )}
        {!loading && !error && !onlyHub && list.length === 0 && q.trim().length >= 2 && (
          <p className="py-6 text-center text-sm text-muted-foreground" data-testid="place-no-results">
            No matching places. Try a hotel name, brand, address, or ZIP code.
          </p>
        )}
        {list.map((p, i) => (
          <button
            key={p.place_id || `${p.label}-${i}`}
            data-testid={`place-option-${p.label}`}
            onClick={() => choose(p)}
            className="flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition-colors hover:bg-accent"
          >
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent text-brand-600">
              {p.airport ? <Plane className="h-4 w-4" /> : p.station ? <TrainFront className="h-4 w-4" /> : <MapPin className="h-4 w-4" />}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-foreground">{p.label}</span>
              {p.address && <span className="block truncate text-xs text-muted-foreground">{p.address}</span>}
            </span>
          </button>
        ))}
      </div>
    </Modal>
  );
}
